import { AlertCircle, Check, Eye, GitPullRequest, X } from "lucide-react";
import { useEffect, useState } from "react";
import CircleAvatar from "../../../components/CircleAvatar/CircleAvatar";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Separator } from "../../../components/ui/separator";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../components/ui/tabs";
import { SpecialistData } from "../../../types/TypeSpecialist";
import { TypeUserData } from "../../../types/TypeUsers";
import {
  formatTimeZoneWithGMT,
  getDurationString,
} from "../../../utils/dateActions";
import { calculateAge } from "../../../utils/general";
import { getTranslation } from "../../../utils/getTranslation";

interface SpecialistReviewPanelProps {
  specialist: TypeUserData;
  specialistInfo: SpecialistData;
  avatar?: string;
  onApprove: () => Promise<void>;
  onReject: () => Promise<void>;
  onViewProfile: (isPublic: boolean) => void;
  onViewDiff: () => void;
  onClose: () => void;
}

export const SpecialistReviewPanel = ({
  specialist,
  specialistInfo,
  avatar,
  onApprove,
  onReject,
  onViewProfile,
  onViewDiff,
  onClose,
}: SpecialistReviewPanelProps) => {
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only handle shortcuts if no input is focused
      if (
        document.activeElement?.tagName === "INPUT" ||
        document.activeElement?.tagName === "TEXTAREA"
      ) {
        return;
      }

      switch (e.key) {
        case "a":
          if (e.altKey || e.metaKey) {
            e.preventDefault();
            handleApprove();
          }
          break;
        case "r":
          if (e.altKey || e.metaKey) {
            e.preventDefault();
            handleReject();
          }
          break;
        case "d":
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            onViewDiff();
          }
          break;
        case "Escape":
          onClose();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, []);

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      await onApprove();
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    setIsRejecting(true);
    try {
      await onReject();
    } finally {
      setIsRejecting(false);
    }
  };

  const needsReview =
    !specialistInfo.isVerifiedChanges ||
    specialistInfo.servicesConfig.reviewRequested ||
    specialistInfo.reviewRequested;

  return (
    <div className="dark h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <CircleAvatar
              name={getTranslation(specialist.name)}
              surname={getTranslation(specialist.surname)}
              avatarProps={avatar}
            />
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {getTranslation(specialist.name)}{" "}
                  {getTranslation(specialist.surname)}
                </h2>
                {specialistInfo.isVerified && (
                  <Badge variant="success" className="gap-1">
                    <Check className="h-3 w-3" />
                    Verified
                  </Badge>
                )}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {specialist.email}
              </p>
            </div>
          </div>
        </div>

        {/* Review Status */}
        {needsReview && (
          <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                Review Required
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-400">
                {!specialistInfo.isVerifiedChanges && "Profile changes • "}
                {specialistInfo.servicesConfig.reviewRequested &&
                  "Service config • "}
                {specialistInfo.reviewRequested && "Ready for review"}
              </p>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="flex items-center gap-2 mt-4">
          <Button
            variant="success"
            size="sm"
            onClick={handleApprove}
            disabled={isApproving || isRejecting}
            className="gap-2"
          >
            <Check className="h-4 w-4" />
            Approve
            <kbd className="ml-1 px-1.5 py-0.5 text-xs font-mono bg-white/20 rounded">
              Alt+A
            </kbd>
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleReject}
            disabled={isApproving || isRejecting}
            className="gap-2"
          >
            <X className="h-4 w-4" />
            Reject
            <kbd className="ml-1 px-1.5 py-0.5 text-xs font-mono bg-white/20 rounded">
              Alt+R
            </kbd>
          </Button>
          <Separator orientation="vertical" className="h-6" />
          <Button
            variant="outline"
            size="sm"
            onClick={onViewDiff}
            className="gap-2"
          >
            <GitPullRequest className="h-4 w-4" />
            View Changes
            <kbd className="ml-1 px-1.5 py-0.5 text-xs font-mono bg-gray-100 dark:bg-gray-800 rounded">
              Ctrl+D
            </kbd>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewProfile(true)}
            className="gap-2"
          >
            <Eye className="h-4 w-4" />
            Current Profile
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewProfile(false)}
            className="gap-2"
          >
            <Eye className="h-4 w-4" />
            Updated Profile
          </Button>
        </div>
      </div>

      {/* Content Tabs */}
      <div className="flex-1 dark:bg-gray-800">
        <div className="p-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="services">Services</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Personal Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <InfoItem
                    label="First Name"
                    value={getTranslation(specialist.name)}
                  />
                  <InfoItem
                    label="Last Name"
                    value={getTranslation(specialist.surname)}
                  />
                  <InfoItem label="Email" value={specialist.email} />
                  <InfoItem
                    label="Specialization"
                    value={
                      specialistInfo.mainSpecializations[0]?.specialization ||
                      "-"
                    }
                  />
                  <InfoItem
                    label="Location"
                    value={
                      specialist.location?.includes("undefined")
                        ? "-"
                        : specialist.location || "-"
                    }
                  />
                  <InfoItem
                    label="Age"
                    value={
                      specialist.dateOfBirth
                        ? calculateAge(new Date(specialist.dateOfBirth))
                        : "-"
                    }
                  />
                  <InfoItem
                    label="Time zone"
                    value={
                      specialist.timezone
                        ? formatTimeZoneWithGMT(specialist.timezone)
                        : "-"
                    }
                  />
                  <InfoItem
                    label="Experience"
                    value={
                      specialistInfo.practiceStartYear
                        ? getDurationString(
                            new Date(specialistInfo.practiceStartYear),
                            new Date(),
                            "en"
                          ).text
                        : "-"
                    }
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <StatusItem
                    label="Profile Verified"
                    status={specialistInfo.isVerified}
                  />
                  <StatusItem
                    label="Changes Verified"
                    status={specialistInfo.isVerifiedChanges}
                  />
                  <StatusItem
                    label="Service Review Requested"
                    status={!specialistInfo.servicesConfig.reviewRequested}
                    inverted
                  />
                  <StatusItem
                    label="Review Requested"
                    status={!specialistInfo.reviewRequested}
                    inverted
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="details" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Professional Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <InfoItem
                    label="Main Specialization"
                    value={
                      specialistInfo.mainSpecializations
                        .map((s) => s.specialization)
                        .join(", ") || "-"
                    }
                  />
                  <InfoItem
                    label="Sub Specializations"
                    value={
                      specialistInfo.subSpecializations
                        .map((s) => s.specialization)
                        .join(", ") || "-"
                    }
                  />
                  <InfoItem
                    label="Languages"
                    value={specialistInfo.preferredLanguages?.join(", ") || "-"}
                  />
                  <InfoItem
                    label="Practice Hours"
                    value={specialistInfo.practiceHours?.toString() || "-"}
                  />
                  <InfoItem
                    label="Level"
                    value={specialistInfo.level?.level || "-"}
                  />
                  <InfoItem
                    label="Tax Category"
                    value={specialistInfo.taxCategory || "-"}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="services" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Service Configuration
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <ServiceItem
                    label="Show in Catalog"
                    config={specialistInfo.servicesConfig.showInCatalog}
                  />
                  <ServiceItem
                    label="Show in Specialist Matching"
                    config={specialistInfo.servicesConfig.showInTreeQuiz}
                  />
                  <ServiceItem
                    label="Show in Subscription"
                    config={specialistInfo.servicesConfig.showInSubscription}
                  />
                  <ServiceItem
                    label="Show in Projects"
                    config={specialistInfo.servicesConfig.showInProjects}
                  />
                  <ServiceItem
                    label="Show in Barter"
                    config={specialistInfo.servicesConfig.showInBarter}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const InfoItem = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => (
  <div className="flex flex-col">
    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
      {label}
    </span>
    <span className="text-sm text-gray-900 dark:text-white">{value}</span>
  </div>
);

const StatusItem = ({
  label,
  status,
  inverted = false,
}: {
  label: string;
  status: boolean;
  inverted?: boolean;
}) => {
  const isGood = inverted ? !status : status;
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
        {label}
      </span>
      <Badge variant={isGood ? "success" : "destructive"}>
        {isGood ? "Yes" : "No"}
      </Badge>
      <div className="flex-1"></div>
    </div>
  );
};

const ServiceItem = ({
  label,
  config,
}: {
  label: string;
  config: { switch: boolean; allowed: boolean };
}) => (
  <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
    <div className="flex flex-col">
      <span className="text-sm font-medium text-gray-900 dark:text-white">
        {label}
      </span>
      {config.switch && (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Enabled by user
        </span>
      )}
    </div>
    <Badge variant={config.allowed ? "success" : "secondary"}>
      {config.allowed ? "Allowed" : "Not Allowed"}
    </Badge>
  </div>
);
